import { FC, TouchEvent, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { colors, fontWeights, radii } from '../../theme/tokens';
import { imageSrc } from './api';
import PageThumbnail, { isPdf } from './PageThumbnail';
import { DocumentPage, QrSummary } from './types';

// The preview panel of XD screens 4 and 5, turned into a
// page navigator (ROADMAP.md, Part 3): the current page with the three
// controls stacked to its right (zoom +, zoom −, full screen), the
// "‹ Page 2 / 5 ›" navigation below and the thumbnail strip.
//
// Per-page QR indicator: the thumbnails with a code carry a mark; when the
// pages carry different codes (`mismatch`), the notice says so here, ahead of
// the confirmation the last step asks for.
//
// Mobile (ROADMAP.md, Part 2): the preview is a thumbnail that opens the viewer
// full screen, with zoom and page change by swiping; the thumbnail
// strip is horizontal.
//
// A PDF is not rasterised (M1 pending): the page is shown with the browser's
// viewer (<iframe>); the zoom enlarges the frame inside its scrollable box.
//
// `tone="light"` is the same viewer over a light background (the document record
// at /document/:id): only the navigation colours change. Without `qr` the
// code state is not shown (the record already carries the document's URL de
// vérification, which is what counts once archived).

export type ViewerTone = 'primary' | 'light';

type Props = {
  pages: DocumentPage[];
  qr?: QrSummary;
  /** Index of the displayed page; controlled by the parent so that steps 2 and 3 share it. */
  index: number;
  onIndexChange: (index: number) => void;
  /** Only the thumbnail strip, without preview (step 3 summary on mobile). */
  compact?: boolean;
  /** Background the viewer sits on: the modal's blue (default) or light. */
  tone?: ViewerTone;
  /** Height of the page on desktop; by default that of the XD panel. */
  canvasHeight?: string;
};

const ZOOM_STEPS = [1, 1.5, 2, 3];
const SWIPE_THRESHOLD = 48;

const zoomIn = (scale: number) =>
  ZOOM_STEPS.find((step) => step > scale) ?? scale;
const zoomOut = (scale: number) =>
  [...ZOOM_STEPS].reverse().find((step) => step < scale) ?? scale;

// The page at a given zoom, inside a scrollable box.
const PageCanvas: FC<{
  page: DocumentPage;
  scale: number;
  number: number;
  dark?: boolean;
  fill?: boolean;
  height?: string;
  onSwipe?: (direction: -1 | 1) => void;
}> = ({
  page,
  scale,
  number,
  dark = false,
  fill = false,
  height = 'clamp(240px, 40vh, 420px)',
  onSwipe,
}) => {
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 1) setTouchStartX(event.touches[0].clientX);
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX === null || !onSwipe || scale !== 1) return;
    const delta = event.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) >= SWIPE_THRESHOLD) onSwipe(delta < 0 ? 1 : -1);
    setTouchStartX(null);
  };

  return (
    <Box
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        position: 'relative',
        width: '100%',
        height: fill ? '100%' : height,
        overflow: 'auto',
        borderRadius: fill ? 0 : `${radii.card}px`,
        backgroundColor: dark ? '#000' : colors.border,
        display: 'flex',
        alignItems: scale === 1 ? 'center' : 'flex-start',
        justifyContent: scale === 1 ? 'center' : 'flex-start',
      }}
    >
      {isPdf(page) ? (
        <iframe
          src={imageSrc(page.image_url)}
          title={`Page ${number}`}
          // The browser's PDF viewer has no zoom we can drive, so the frame
          // itself grows inside the scrollable box and the viewer re-fits
          // the page to the wider frame.
          style={{
            flex: '0 0 auto',
            width: `${scale * 100}%`,
            height: `${scale * 100}%`,
            border: 'none',
          }}
        />
      ) : (
        <img
          src={imageSrc(page.image_url)}
          alt={`Page ${number}`}
          style={{
            maxWidth: scale === 1 ? '100%' : 'none',
            maxHeight: scale === 1 ? '100%' : 'none',
            width: scale === 1 ? 'auto' : `${scale * 100}%`,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
};

const ZoomControls: FC<{
  scale: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFullscreen?: () => void;
  row?: boolean;
}> = ({ scale, onZoomIn, onZoomOut, onFullscreen, row = false }) => {
  const buttonSx = {
    color: colors.white,
    backgroundColor: colors.accent,
    '&:hover': { backgroundColor: colors.accentLight },
    '&.Mui-disabled': { color: colors.white, backgroundColor: colors.accent, opacity: 0.4 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: row ? 'row' : 'column', gap: 1 }}>
      <IconButton aria-label="Zoom avant" onClick={onZoomIn} disabled={scale >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} sx={buttonSx}>
        <AddIcon />
      </IconButton>
      <IconButton aria-label="Zoom arrière" onClick={onZoomOut} disabled={scale <= ZOOM_STEPS[0]} sx={buttonSx}>
        <RemoveIcon />
      </IconButton>
      {onFullscreen && (
        <IconButton aria-label="Plein écran" onClick={onFullscreen} sx={buttonSx}>
          <FullscreenIcon />
        </IconButton>
      )}
    </Box>
  );
};

const NAV_SX: Record<ViewerTone, object> = {
  primary: { color: colors.white, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } },
  light: { color: colors.primary, '&.Mui-disabled': { color: colors.border } },
};

const PageNav: FC<{
  index: number;
  count: number;
  onChange: (index: number) => void;
  tone?: ViewerTone;
}> = ({ index, count, onChange, tone = 'primary' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
    <IconButton
      aria-label="Page précédente"
      size="small"
      disabled={index <= 0}
      onClick={() => onChange(index - 1)}
      sx={NAV_SX[tone]}
    >
      <ChevronLeftIcon />
    </IconButton>
    <Typography data-testid="page-nav" sx={{ fontSize: '0.9375rem', minWidth: 96, textAlign: 'center' }}>
      Page {index + 1} / {count}
    </Typography>
    <IconButton
      aria-label="Page suivante"
      size="small"
      disabled={index >= count - 1}
      onClick={() => onChange(index + 1)}
      sx={NAV_SX[tone]}
    >
      <ChevronRightIcon />
    </IconButton>
  </Box>
);

export const ThumbnailStrip: FC<{
  pages: DocumentPage[];
  index: number;
  onSelect: (index: number) => void;
}> = ({ pages, index, onSelect }) => (
  <Box
    role="list"
    aria-label="Miniatures des pages"
    sx={{ display: 'flex', gap: 1, overflowX: 'auto', py: 0.5, px: 0.25 }}
  >
    {pages.map((page, i) => (
      <ButtonBase
        key={page.id}
        role="listitem"
        aria-label={`Afficher la page ${i + 1}`}
        aria-current={i === index ? 'true' : undefined}
        onClick={() => onSelect(i)}
        sx={{
          position: 'relative',
          flex: '0 0 auto',
          borderRadius: `${radii.card}px`,
          outline: i === index ? `3px solid ${colors.accent}` : '3px solid transparent',
          outlineOffset: 1,
        }}
      >
        <PageThumbnail page={page} alt={`Page ${i + 1}`} size={44} />
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            bottom: 2,
            left: 2,
            px: 0.5,
            borderRadius: 1,
            fontSize: '0.625rem',
            lineHeight: 1.6,
            fontWeight: fontWeights.semiBold,
            color: colors.white,
            backgroundColor: 'rgba(24, 28, 53, 0.75)',
          }}
        >
          {i + 1}
        </Typography>
        {page.verification_url && (
          <QrCode2Icon
            aria-label="QR code détecté"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: 16,
              color: colors.white,
              backgroundColor: colors.accent,
              borderRadius: '3px',
            }}
          />
        )}
      </ButtonBase>
    ))}
  </Box>
);

export const QrStatus: FC<{ qr: QrSummary; pages: DocumentPage[] }> = ({ qr, pages }) => {
  if (qr.status === 'mismatch') {
    return (
      <Alert severity="warning" role="status">
        Les pages portent {qr.urls.length} QR codes différents : vérifiez
        qu'elles appartiennent toutes au même document.
      </Alert>
    );
  }

  const marked = pages.filter((page) => Boolean(page.verification_url)).length;

  return (
    <Typography data-testid="qr-status" sx={{ fontSize: '0.8125rem', opacity: 0.85 }}>
      {qr.status === 'consistent'
        ? `QR code de vérification lu sur ${marked} page${marked > 1 ? 's' : ''} sur ${pages.length}.`
        : 'Aucun QR code de vérification détecté sur ces pages.'}
    </Typography>
  );
};

// Full-screen viewer: zoom, page change by swiping (or
// arrows), and ✕ to go back.
const FullscreenViewer: FC<{
  open: boolean;
  pages: DocumentPage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}> = ({ open, pages, index, onIndexChange, onClose }) => {
  const [scale, setScale] = useState(1);
  const page = pages[index];

  useEffect(() => {
    if (open) setScale(1);
  }, [open, index]);

  const swipe = (direction: -1 | 1) => {
    const next = index + direction;
    if (next >= 0 && next < pages.length) onIndexChange(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { backgroundColor: '#000', color: colors.white } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            backgroundColor: colors.primary,
          }}
        >
          <PageNav index={index} count={pages.length} onChange={onIndexChange} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ZoomControls
              scale={scale}
              onZoomIn={() => setScale(zoomIn)}
              onZoomOut={() => setScale(zoomOut)}
              row
            />
            <IconButton aria-label="Fermer" onClick={onClose} sx={{ color: colors.white }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {page && (
            <PageCanvas page={page} scale={scale} number={index + 1} dark fill onSwipe={swipe} />
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

const PageViewer: FC<Props> = ({
  pages,
  qr,
  index,
  onIndexChange,
  compact = false,
  tone = 'primary',
  canvasHeight,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const [scale, setScale] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  const safeIndex = Math.min(Math.max(index, 0), Math.max(pages.length - 1, 0));
  const page = pages[safeIndex];

  useEffect(() => {
    setScale(1);
  }, [safeIndex]);

  if (!page) {
    return (
      <Typography sx={{ opacity: 0.85 }}>Aucune page pour le moment.</Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {!compact && (
        isMobile ? (
          <ButtonBase
            aria-label="Ouvrir la page en plein écran"
            onClick={() => setFullscreen(true)}
            sx={{
              alignSelf: 'center',
              borderRadius: `${radii.card}px`,
              outline: `2px solid ${colors.accent}`,
              outlineOffset: 2,
            }}
          >
            <PageThumbnail page={page} alt={`Page ${safeIndex + 1}`} size={120} />
          </ButtonBase>
        ) : (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PageCanvas
                page={page}
                scale={scale}
                number={safeIndex + 1}
                height={canvasHeight}
              />
            </Box>
            <ZoomControls
              scale={scale}
              onZoomIn={() => setScale(zoomIn)}
              onZoomOut={() => setScale(zoomOut)}
              onFullscreen={() => setFullscreen(true)}
            />
          </Box>
        )
      )}

      <PageNav index={safeIndex} count={pages.length} onChange={onIndexChange} tone={tone} />
      <ThumbnailStrip pages={pages} index={safeIndex} onSelect={onIndexChange} />
      {qr && <QrStatus qr={qr} pages={pages} />}

      <FullscreenViewer
        open={fullscreen}
        pages={pages}
        index={safeIndex}
        onIndexChange={onIndexChange}
        onClose={() => setFullscreen(false)}
      />
    </Box>
  );
};

export default PageViewer;
